import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Form, Button, Card, Typography, Upload, Tag, Divider } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useStep } from '../../hooks/useStep';
import { axiosInstance } from '../../utils/axios';
import { uploadFile } from '../../utils/uploadFile';
import { useTitle } from '../../hooks/useTitle';
import CommentField from '../CommentField/CommentField';
import { useMessage } from '../../hooks/useMessage';
import LoadingSpinner from './util/LoadingSpinner';
import { centerPopup } from '../../utils/centerPopup';
import { ScreeningContentContext } from '../../context/ScreeningContentProvider';
import { useTranslation } from 'react-i18next';

const { Paragraph } = Typography;

interface BkrInputs {
  comment: string;
}

const BKRRegistrationStep = forwardRef((props, ref) => {
  const { t } = useTranslation(['screening/bkr', 'screening/general']);
  useTitle(t('Title'));
  const [form] = Form.useForm();
  const { toast } = useMessage();
  const { goToNextStep } = useStep();
  const [file, setFile] = useState<File | null>(null);
  const [existingFile, setExistingFile] = useState<boolean>(false);

  const content = useContext(ScreeningContentContext);
  const loadingContent = content === 'loading';
  const [loadingData, setLoadingData] = useState(true);
  const [loadingSaveFile, setLoadingSaveFile] = useState(false);
  const [comment, setComment] = useState<string>('');

  const openBkr = () => {
    const link = !loadingContent ? content.bkrCheck.bkrLink : 'alt';
    const duoWindow = centerPopup(link as string, '_blank', 1080, 800);
    if (duoWindow) {
      duoWindow.focus();
    }
    if (!duoWindow) {
      toast.error(t('EM-BKRPopupFailed'));
    }
  };

  useImperativeHandle(ref, () => ({
    save() {
      const values = form.getFieldsValue();
      const touched = form.isFieldsTouched();
      if (!touched && !file) {
        return;
      }
      form
        .validateFields({ validateOnly: true })
        .then(() => {
          handleSubmit(values, false);
        })
        .catch(() => {});
    },
  }));

  const handleSubmit = async (values: BkrInputs, nextStep: boolean) => {
    setLoadingSaveFile(true);
    if (file) {
      await axiosInstance
        .get('/screening/bkr/file/url')
        .then((response) => {
          const fileUrl = response.data.url;
          const path = response.data.filePath;

          if (!fileUrl) {
            toast.error(t('EM-FileSaveError'));
            return;
          }

          uploadFile(file, fileUrl)
            .then(() => {
              axiosInstance
                .post('screening/bkr/savefile', {
                  path,
                })
                .then(async () => {
                  toast.success(t('M-FileSaved', { ns: 'screening/general' }));
                  await goToNextStep();
                })
                .catch(() => {
                  toast.error(t('EM-FileSaveError'));
                  setLoadingSaveFile(false);
                  return;
                });
            })
            .catch(() => {
              toast.error(t('EM-FileSaveError'));
              setLoadingSaveFile(false);
              return;
            });
        })
        .catch(() => {
          toast.error(t('EM-GeneralError'));
          setLoadingSaveFile(false);
          return;
        });
      toast.success(t('M-FileSaveSuccess'));
      setLoadingSaveFile(false);
    } else if (existingFile) {
      if (nextStep) goToNextStep();
    } else {
      toast.warning(t('W-NoFileSelected'));
      if (nextStep) goToNextStep();
    }
    setLoadingSaveFile(false);
  };

  useEffect(() => {
    axiosInstance
      .get('/screening/bkr/comment')
      .then((response) => {
        if (response) {
          setComment(response.data);
        }
        setLoadingData(false);
      })
      .catch(() => {
        toast.error(t('EM-GeneralError', { ns: 'screening/general' }));
        setLoadingData(false);
      });
    axiosInstance
      .get('/screening/bkr/file/existing')
      .then((response) => {
        if (response && response.data != null) {
          setExistingFile(response.data);
        }
      })
      .catch(() => {
        toast.error(t('EM-GeneralError', { ns: 'screening/general' }));
      });
  }, [toast, t]);

  return loadingData || loadingContent ? (
    <LoadingSpinner />
  ) : (
    <div>
      <Form
        form={form}
        onFinish={(v) => handleSubmit(v, false)}
        layout="vertical"
      >
        <Card title={content.bkrCheck.title} style={{ maxWidth: 500 }}>
          <Paragraph>{content.bkrCheck.description}</Paragraph>

          <Button onClick={() => openBkr()} type="primary">
            {t('B-OpenBKR')}
          </Button>

          <Divider />

          <Form.Item label={content.bkrCheck.bkrRequest} name="fileUpload">
            <Upload
              beforeUpload={(file) => {
                setFile(file);
                return false;
              }}
              multiple={false}
              maxCount={1}
              accept=".pdf"
            >
              {existingFile && (
                <div>
                  <Tag color="success">{t('L-FileUploaded')}</Tag>
                  <Paragraph italic>{t('P-ReuploadPossible')}</Paragraph>
                </div>
              )}
              <Button
                icon={<UploadOutlined />}
                type={existingFile ? 'primary' : 'default'}
              >
                {t('B-SelectFile', { ns: 'screening/general' })}
              </Button>
            </Upload>
          </Form.Item>
          <Divider />
          <CommentField ApiUrl={'screening/bkr/comment'} comment={comment} />
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loadingSaveFile}>
              {t('B-Save', { ns: 'screening/general' })}
            </Button>
          </Form.Item>
        </Card>
        <Button
          style={{
            float: 'right',
            marginTop: 20,
            marginBottom: 30,
            marginRight: 10,
          }}
          type="primary"
          onClick={() => {
            const values = form.getFieldsValue();
            form
              .validateFields()
              .then(() => {
                handleSubmit(values, true);
              })
              .catch(() => {});
          }}
        >
          {t('B-Next', { ns: 'screening/general' })}
        </Button>
      </Form>
    </div>
  );
});

export default BKRRegistrationStep;
